import React from 'react';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { ErrorPayload } from '../../redux/reducers/errors';
import { batch } from 'react-redux';
import { clsx } from '../../utils/htmlUtils';

/**
 * A component to render a human-readable error message from CoreAPIService
 */
interface ErrorProps {
    payload: any;
}

const ErrorMessage = (props: ErrorProps): JSX.Element => {
    const classes = useStyles();
    const { payload } = props;

    const [isClosed, setIsClosed] = React.useState<boolean>(false);
    const [message, setMessage] = React.useState<string>('');

    React.useEffect(() => {
        if (payload) {
            const parsedErrorMessage: string = parseErrorMessage();
            if (parsedErrorMessage !== message) {
                batch(() => {
                    setIsClosed(false);
                    setMessage(parsedErrorMessage);
                });
            }
        } else {
            batch(() => {
                setIsClosed(true);
                setMessage('');
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payload]);

    // Scroll to show error when opened
    React.useEffect(() => {
        if (!isClosed) {
            const errorElement = document.querySelector('.snackError');
            if (errorElement) {
                const elementPosition = errorElement.getBoundingClientRect().top + window.scrollY - 150;
                window.scrollTo({ top: elementPosition, behavior: 'smooth' });
            }
        }
    }, [isClosed]);

    //TODO: close + reset message after some seconds to allow same payload to show the message again
    const closeMessage = (): void => setIsClosed(true);

    const parseErrorMessage = (): string => {
        const {
            error,
            error_description,
            description,
            localized_error,
            details,
            detail,
            message: errorMessage,
        }: ErrorPayload = payload;

        const getErrorDetails = (details: any): string => {
            if (details instanceof Object) {
                return (Object.entries(details) as [string, any][])
                    .map(([key, value]) => `(${key}: ${value?.description || ''})`)
                    .join(' ');
            }
            return details;
        };

        const getITMessage = (): string => {
            if (!error) {
                return '';
            }
            return typeof error === 'object' ? ` (${JSON.stringify(error)})` : ` (${error})`;
        };

        const errorMessageParsed =
            error_description ||
            description ||
            detail ||
            (details && getErrorDetails(details)) ||
            localized_error ||
            errorMessage ||
            '';

        return errorMessageParsed + getITMessage();
    };

    const ActionButton: JSX.Element = (
        <Button className={classes.closeButton} onClick={closeMessage}>
            <Icon fontSize="small">close</Icon>
        </Button>
    );

    if (isClosed || !message) {
        return null;
    }
    return <SnackbarContent className={clsx(classes.snackbar, 'snackError')} message={message} action={ActionButton} />;
};

//=====================================//
//=============== Style ===============//
//=====================================//

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        snackbar: {
            backgroundColor: theme.palette.error.light,
            color: theme.palette.common.white,
            fontWeight: 'bold',
            minWidth: 0,
            flexWrap: 'nowrap',
        },
        closeButton: {
            color: theme.palette.common.white,
            minWidth: 0,
        },
    }),
);

export default ErrorMessage;
